import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Checkbox } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';
import { getUsuarios, saveUsuario, existeUserName } from '../service/UsuarioService';
import { useTerminologia } from '../hooks/useTerminologia';
import { ROLES } from '../constants/roles';

const HOST_API = process.env.REACT_APP_URL;

const usuarioVacio = {
    usuarioId: 0,
    userName: '',
    nombre: '',
    apellido: '',
    password: '',
    email: '',
    rolId: 1,
    administracionId: parseInt(localStorage.getItem('administracionId') || '1'),
    activo: true,
    barriosPermitidos: [],
    personaId: null,
};

const GestionUsuarios = () => {
    const toast = useRef(null);
    const t = useTerminologia();
    const roles = ROLES.map(r => r.value === 2 ? { ...r, label: `Usuario ${t.Barrio}` } : r);
    const [usuarios, setUsuarios] = useState([]);
    const [barrios, setBarrios] = useState([]);
    const [personas, setPersonas] = useState([]);
    const [form, setForm] = useState(usuarioVacio);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [userNameStatus, setUserNameStatus] = useState('idle'); // idle | checking | available | taken
    const userNameOriginal = useRef('');

    const administracionId = parseInt(localStorage.getItem('administracionId') || '1');

    useEffect(() => {
        cargarUsuarios();
        cargarBarrios();
    }, []);

    const cargarUsuarios = async () => {
        setLoading(true);
        try {
            const res = await getUsuarios(administracionId);
            setUsuarios(res.data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los usuarios', life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    const cargarBarrios = async () => {
        try {
            const res = await axios.get(`${HOST_API}api/barrios/getBarrios?barrioId=0&administracionId=${administracionId}`);
            setBarrios((res.data || []).map(b => ({ label: b.nombreBarrio, value: b.barrioId })));
        } catch {
            // No bloquea si falla
        }
    };

    // Carga personas para los barrios indicados (llamar al abrir diálogo o al cambiar barrios)
    const cargarPersonas = async (barrioIds) => {
        if (!barrioIds || barrioIds.length === 0) {
            setPersonas([]);
            return;
        }
        try {
            const resultados = await Promise.all(
                barrioIds.map(bid =>
                    axios.get(`${HOST_API}api/personas/GetPersonas?administracionId=${administracionId}&personaId=0&barrioId=${bid}`)
                        .then(r => r.data || [])
                        .catch(() => [])
                )
            );
            const flat = resultados.flat();
            const unique = [...new Map(flat.map(p => [p.personaId, p])).values()];
            setPersonas(unique.map(p => ({ label: p.nombreCompleto, value: p.personaId })));
        } catch {
            setPersonas([]);
        }
    };

    const abrirNuevo = () => {
        setForm({ ...usuarioVacio, administracionId });
        setPersonas([]);
        userNameOriginal.current = '';
        setUserNameStatus('idle');
        setDialogVisible(true);
    };

    const abrirEditar = (u) => {
        const barriosDelUsuario = u.barriosPermitidos || [];
        if (u.rolId === 2 && barriosDelUsuario.length > 0) {
            cargarPersonas(barriosDelUsuario);
        } else {
            setPersonas([]);
        }
        setForm({
            usuarioId:         u.usuarioId,
            userName:          u.userName,
            nombre:            u.nombre,
            apellido:          u.apellido,
            password:          '',
            email:             u.email,
            rolId:             u.rolId,
            administracionId:  u.administracionId,
            activo:            u.activo,
            barriosPermitidos: barriosDelUsuario,
            personaId:         u.personaId || null,
        });
        userNameOriginal.current = u.userName;
        setUserNameStatus('idle');
        setDialogVisible(true);
    };

    const handleChange = (field, value) => {
        setForm(prev => {
            const update = { ...prev, [field]: value };
            if (field === 'rolId') update.personaId = null;
            return update;
        });

        if (field === 'barriosPermitidos' && form.rolId === 2) {
            cargarPersonas(value);
        }

        if (field === 'userName') {
            setUserNameStatus('idle');
        }
    };

    const checkUserName = async () => {
        const userName = form.userName.trim();
        if (!userName || userName === userNameOriginal.current) {
            setUserNameStatus('idle');
            return;
        }
        setUserNameStatus('checking');
        try {
            const { data } = await existeUserName(userName, form.usuarioId || 0);
            setUserNameStatus(data.existe ? 'taken' : 'available');
        } catch {
            setUserNameStatus('idle');
        }
    };

    const guardar = async () => {
        if (!form.nombre.trim())    { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingresá el nombre', life: 3000 }); return; }
        if (!form.apellido.trim())  { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingresá el apellido', life: 3000 }); return; }
        if (!form.userName.trim())  { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingresá el usuario', life: 3000 }); return; }
        if (form.usuarioId === 0 && !form.password.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingresá la contraseña', life: 3000 }); return;
        }
        if (form.rolId === 2 && !form.personaId) {
            toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccioná la persona del barrio', life: 3000 }); return;
        }

        const userName = form.userName.trim();
        if (userName !== userNameOriginal.current) {
            setUserNameStatus('checking');
            try {
                const { data } = await existeUserName(userName, form.usuarioId || 0);
                if (data.existe) {
                    setUserNameStatus('taken');
                    toast.current.show({ severity: 'warn', summary: 'Usuario existente', detail: 'Ese usuario (login) ya existe, elegí otro', life: 4000 });
                    return;
                }
                setUserNameStatus('available');
            } catch {
                setUserNameStatus('idle');
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo validar el usuario, intentá de nuevo', life: 3000 });
                return;
            }
        }

        setSaving(true);
        try {
            await saveUsuario(form);
            toast.current.show({ severity: 'success', summary: 'OK', detail: 'Usuario guardado correctamente', life: 3000 });
            setDialogVisible(false);
            cargarUsuarios();
        } catch (err) {
            const msg = err.response?.data?.errorDisplay || 'Error al guardar el usuario';
            toast.current.show({ severity: 'error', summary: 'Error', detail: msg, life: 4000 });
        } finally {
            setSaving(false);
        }
    };

    const accionesTemplate = (u) => (
        <Button
            icon="pi pi-pencil"
            className="p-button-rounded p-button-text p-button-sm"
            onClick={() => abrirEditar(u)}
            tooltip="Editar"
        />
    );

    const activoTemplate = (u) => u.activo
        ? <span className="customer-badge status-ingreso">Activo</span>
        : <span className="customer-badge status-renewal" style={{ background: '#fde8e8', color: '#c0392b' }}>No Activo</span>;

    const rolTemplate = (u) => u.rol?.descripcion || roles.find(r => r.value === u.rolId)?.label || u.rolId;

    const barriosTemplate = (u) => {
        const ids = u.barriosPermitidos || [];
        if (ids.length === 0) return <span style={{ color: '#aaa' }}>—</span>;
        const nombres = ids.map(id => barrios.find(b => b.value === id)?.label || id).join(', ');
        return <span title={nombres}>{nombres}</span>;
    };

    const personaTemplate = (u) => {
        if (!u.personaId) return <span style={{ color: '#aaa' }}>—</span>;
        return personas.find(p => p.value === u.personaId)?.label || `(id: ${u.personaId})`;
    };

    const footerDialog = (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={() => setDialogVisible(false)} disabled={saving} />
            <Button label="Guardar" icon="pi pi-check" onClick={guardar} loading={saving} />
        </div>
    );

    return (
        <>
            <h4>Gestión de Usuarios</h4>
            <br />

            <Toast ref={toast} />

            <div className="flex align-items-center gap-3 mb-3">
                <Button label="Nuevo usuario" icon="pi pi-plus" onClick={abrirNuevo} />
            </div>

            <div className="grid">
                <div className="col-12">
                    <DataTable
                        value={usuarios}
                        loading={loading}
                        paginator
                        rows={10}
                        emptyMessage="No hay usuarios"
                        responsiveLayout="scroll"
                        className="bg-white"
                    >
                        <Column field="nombre"   header="Nombre"   sortable />
                        <Column field="apellido" header="Apellido" sortable />
                        <Column field="userName" header="Usuario"  sortable />
                        <Column field="email"    header="Email"    sortable />
                        <Column header="Rol"     body={rolTemplate} />
                        <Column header={t.Barrios} body={barriosTemplate} />
                        <Column header="Persona vinculada" body={personaTemplate} />
                        <Column header="Estado"  body={activoTemplate} style={{ width: '100px' }} />
                        <Column header=""        body={accionesTemplate} style={{ width: '60px' }} />
                    </DataTable>
                </div>
            </div>

            <Dialog
                header={form.usuarioId === 0 ? 'Nuevo usuario' : 'Editar usuario'}
                visible={dialogVisible}
                style={{ width: '480px' }}
                onHide={() => setDialogVisible(false)}
                footer={footerDialog}
            >
                <div className="p-fluid flex flex-column gap-3 mt-2">
                    <div className="flex gap-3">
                        <div className="flex-1">
                            <label className="block mb-1">Nombre *</label>
                            <InputText value={form.nombre} onChange={e => handleChange('nombre', e.target.value)} />
                        </div>
                        <div className="flex-1">
                            <label className="block mb-1">Apellido *</label>
                            <InputText value={form.apellido} onChange={e => handleChange('apellido', e.target.value)} />
                        </div>
                    </div>

                    <div>
                        <label className="block mb-1">Usuario (login) *</label>
                        <InputText
                            value={form.userName}
                            onChange={e => handleChange('userName', e.target.value)}
                            onBlur={checkUserName}
                            className={userNameStatus === 'taken' ? 'p-invalid' : ''}
                        />
                        {userNameStatus === 'checking' && (
                            <small style={{ color: '#888' }}>Verificando disponibilidad...</small>
                        )}
                        {userNameStatus === 'available' && (
                            <small style={{ color: '#12b76a' }}>✓ Disponible</small>
                        )}
                        {userNameStatus === 'taken' && (
                            <small style={{ color: '#f04438' }}>✗ Ese usuario ya existe</small>
                        )}
                    </div>

                    <div>
                        <label className="block mb-1">Email</label>
                        <InputText value={form.email} onChange={e => handleChange('email', e.target.value)} />
                    </div>

                    <div>
                        <label className="block mb-1">
                            Contraseña {form.usuarioId > 0 && <span style={{ color: '#888', fontSize: '0.8rem' }}>(dejar vacío para no cambiar)</span>}
                            {form.usuarioId === 0 && ' *'}
                        </label>
                        <InputText
                            type="password"
                            value={form.password}
                            onChange={e => handleChange('password', e.target.value)}
                            autoComplete="new-password"
                        />
                    </div>

                    <div>
                        <label className="block mb-1">Rol *</label>
                        <Dropdown
                            value={form.rolId}
                            options={roles}
                            onChange={e => handleChange('rolId', e.value)}
                            placeholder="Seleccioná un rol"
                        />
                    </div>

                    {(form.rolId === 2 || form.rolId === 3) && (
                        <div>
                            <label className="block mb-1">{t.Barrios} habilitados</label>
                            <MultiSelect
                                value={form.barriosPermitidos}
                                options={barrios}
                                onChange={e => handleChange('barriosPermitidos', e.value)}
                                placeholder={`Seleccioná ${t.barrios}`}
                                display="chip"
                                filter
                            />
                        </div>
                    )}

                    {form.rolId === 2 && (
                        <div>
                            <label className="block mb-1">Persona del barrio *</label>
                            <Dropdown
                                value={form.personaId}
                                options={personas}
                                onChange={e => handleChange('personaId', e.value)}
                                placeholder={form.barriosPermitidos.length === 0 ? 'Primero seleccioná los barrios' : 'Seleccioná una persona'}
                                disabled={form.barriosPermitidos.length === 0}
                                filter
                                filterPlaceholder="Buscar..."
                                emptyMessage="No hay personas en estos barrios"
                            />
                        </div>
                    )}

                    <div className="flex align-items-center gap-2 mt-1">
                        <Checkbox
                            inputId="activo"
                            checked={form.activo}
                            onChange={e => handleChange('activo', e.checked)}
                        />
                        <label htmlFor="activo">Usuario activo</label>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default GestionUsuarios;
